import type { OutboundEmailFields } from '../components/CatoContainer/Pages/Email/Outbound/EmailOutboundHeader';
import type { DraftEmailContact } from '@amazon-connect/email';
import { EmailContactData } from '../models/EmailContact';

// The amazon-connect-streams type definitions don't include emailAddress on endpoint
// This interface encapsulates the shape until the types are updated upstream.

interface EmailEndpoint {
  emailAddress?: string;
  // The type definition exposes 'name' but the runtime object uses 'displayName'
  displayName?: string;
}

// The amazon-connect-streams type definitions declare 'valueString' but the runtime
// uses 'ValueString'
interface SegmentAttributeValue {
  ValueString?: string;
}

export const getCustomerDisplayName = (contact: connect.Contact): string | undefined =>
  (contact.getCustomerEndpoint() as EmailEndpoint)?.displayName;

// Connect and the email API both reject an empty subject. Mail clients ignore a "Re:" prefix when matching subjects,
// so a bare "Re:" is non-empty yet still matches the original empty subject and keeps the reply in its thread.
const EMPTY_REPLY_SUBJECT = 'Re:';

// Connect gives an empty subject when the customer sent none. Subjects are normalized where they enter the app,
// so a missing subject is always `undefined` and never an empty string; only the reply send applies a default.
export const normalizeSubject = (subject: string | null | undefined): string | undefined =>
  subject?.trim() || undefined;

export const replySubject = (subject: string | undefined): string => subject ?? EMPTY_REPLY_SUBJECT;

export const extractEmailData = (contact: connect.Contact): EmailContactData => {
  const contactId = contact.getContactId();
  const segmentAttributes = contact.getSegmentAttributes();

  const customerName = getCustomerDisplayName(contact);
  const from = (contact.getCustomerEndpoint() as EmailEndpoint)?.emailAddress;
  const systemEndpoint = contact.getConnectSystemEndpoint();
  const toAddress = (systemEndpoint as EmailEndpoint)?.emailAddress;
  const to = toAddress ? [toAddress] : undefined;
  const subject = normalizeSubject((segmentAttributes['connect:EmailSubject'] as SegmentAttributeValue)?.ValueString);
  const receivedAt = contact.getState().timestamp?.toISOString();

  const initiationMethod = contact.getInitiationMethod() ?? undefined;
  const contactAssociationId = contact.getContactAssociationId() ?? undefined;

  return { contactId, contactAssociationId, customerName, from, to, subject, receivedAt, initiationMethod };
};

export const formatReceivedAt = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();

  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return isToday ? `Today, ${day} at ${time}` : `${day} at ${time}`;
};

export const formatBodyPreview = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
};

// New emails go straight to Connect through the Streams SDK, whose cc accepts at most 10 addresses.
export const MAX_OUTBOUND_OTHER_RECIPIENTS = 10;

export const isOutboundFieldsValid = ({ to, otherRecipients, subject }: OutboundEmailFields): boolean =>
  to.length === 1 && otherRecipients.length <= MAX_OUTBOUND_OTHER_RECIPIENTS && subject.trim() !== '';

export const toOutboundEmailRecipients = ({
  to,
  otherRecipients
}: OutboundEmailFields): Pick<DraftEmailContact, 'to' | 'cc'> => ({
  to: to.map(emailAddress => ({ emailAddress })),
  cc: otherRecipients.length ? otherRecipients.map(emailAddress => ({ emailAddress })) : undefined
});
